/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentProps, Ref } from 'preact';
import { forwardRef } from 'preact/compat';
import { useCallback, useImperativeHandle, useRef } from 'preact/hooks';

import { FocusableHandle, useFocusableTextField } from '#hooks/UNSAFE_useFocusableTextField';
import { useFormContext } from '#hooks/UNSAFE_useFormContext';
import { FormFieldContext } from '#hooks/UNSAFE_useFormFieldContext';
import { useTextField } from '#hooks/UNSAFE_useTextField';
import { Label } from '#UNSAFE_Label';
import { PrefixSuffix } from '#UNSAFE_PrefixSuffix';
import { usePrefixSuffix } from '#hooks/UNSAFE_usePrefixSuffix';
import {
  ReadonlyTextField,
  ReadonlyTextFieldInput,
  TextField,
  TextFieldInput
} from '#UNSAFE_TextField';
import {
  InlineUserAssistance,
  CompactUserAssistance,
  UserAssistanceDensityType
} from '#UNSAFE_UserAssistance';
import { getClientHints } from '#utils/PRIVATE_clientHints';
import { Size } from '#utils/UNSAFE_size';
import { ValueUpdateDetail } from '#utils/UNSAFE_valueUpdateDetail';
import { useCurrentValueReducer } from '#hooks/UNSAFE_useCurrentValueReducer';
import { DecrementIcon } from '#PRIVATE_ThemedIcons/DecrementIcon';
import { IncrementIcon } from '#PRIVATE_ThemedIcons/IncrementIcon';
import { MinusIcon } from '#PRIVATE_ThemedIcons/MinusIcon';
import { PlusIcon } from '#PRIVATE_ThemedIcons/PlusIcon';
import { StepButton } from './StepButton';
import { Steppers } from './Steppers';
import { useSpinning, StepEvent } from '#hooks/UNSAFE_useSpinning';
import { LayoutColumnSpan } from '#utils/UNSAFE_styles/Layout';
import type { TestIdProps } from '#hooks/UNSAFE_useTestId';
import { SelectableHandle, useSelectableTextField } from '#hooks/UNSAFE_useSelectableTextField';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { defaultTextAlignForVariant } from '#utils/PRIVATE_formControlUtils';

type TextFieldInputProps = ComponentProps<typeof TextFieldInput>;

type InlineUserAssistanceProps = ComponentProps<typeof InlineUserAssistance>;

type NumberInputTextProps = {
  /**
   * Controls whether the step down button is disabled.
   */
  isStepDownDisabled?: boolean;

  /**
   * Controls whether the step up button is disabled.
   */
  isStepUpDisabled?: boolean;

  /**
   * Called when a continuous step is requested (aka "spin")
   * by holding down the arrow up or arrow down keys, or
   * by pressing and holding down a step button.
   */
  onSpin?: (event: StepEvent) => void;

  /**
   * Called when spinning is complete and the user has
   * released the arrow key or step button.
   */
  onSpinComplete?: () => void;

  /**
   * Called when a step is requested by clicking a step button
   * or by pressing the arrow up or arrow down keys.
   */
  onStep?: (event: StepEvent) => void;
};

type Props = NumberInputTextProps &
  TestIdProps & {
    /**+
     * The ID of an element (or space separated IDs of multiple elements) that
     * describes the input.
     */
    'aria-describedby'?: TextFieldInputProps['aria-describedby'];

    /**
     * The maximum allowed value when hasSteppers is true. Used for the aria-valuemax attribute.
     * When hasSteppers is false, this is ignored because when there are no steppers, there is no role="spinbutton",
     * and aria-value* attributes are not valid without this role.
     */
    'aria-valuemax'?: TextFieldInputProps['aria-valuemax'];

    /**
     * The minimum allowed value when hasSteppers is true. Used for the aria-valuemin attribute.
     * When hasSteppers is false, this is ignored because when there are no steppers, there is no role="spinbutton",
     * and aria-value* attributes are not valid without this role.
     */
    'aria-valuemin'?: TextFieldInputProps['aria-valuemin'];

    /**
     * The number value of the component when hasSteppers is true. Used for the aria-valuenow attribute.
     * When hasSteppers is false, this is ignored because when there are no steppers, there is no role="spinbutton",
     * and aria-value* attributes are not valid without this role.
     */
    'aria-valuenow'?: TextFieldInputProps['aria-valuenow'];

    /**
     * The formatted value of the component when hasSteppers is true. Used for the aria-valueText
     * attribute to provide a more human readable text alternative for
     * aria-valuenow.
     * When hasSteppers is false, this is ignored because when there are no steppers, there is no role="spinbutton",
     * and aria-value* attributes are not valid without this role.
     */
    'aria-valuetext'?: TextFieldInputProps['aria-valuetext'];

    /**
     * Text to provide guidance to help the user understand what data to enter.
     */
    assistiveText?: InlineUserAssistanceProps['assistiveText'];

    /**
     * Dictates component's autocomplete state.
     * This attribute indicates whether the value of the control can be automatically completed by the browser.
     * The common values are "on" and "off".
     */
    autoComplete?: TextFieldInputProps['autoComplete'];

    /**
     * Autofocus specifies whether the component will get focus when the page is loaded.
     * If it is set to true then the associated component will get input focus when the page is loaded.
     * Setting this property doesn't set the focus to the component: it tells the browser to focus
     * to it when the element is inserted in the document.
     */
    autoFocus?: TextFieldInputProps['autoFocus'];

    /**
     * Specifies how many columns to span in a FormLayout with direction === 'row'
     */
    columnSpan?: LayoutColumnSpan;

    /**
     * Controls whether the step buttons are shown.
     */
    hasSteppers?: boolean;

    /**
     * Help source URL associated with the component.
     */
    helpSourceLink?: InlineUserAssistanceProps['helpSourceLink'];

    /**
     * Custom text to be rendered for the <code>helpSourceLink</code>.
     */
    helpSourceText?: InlineUserAssistanceProps['helpSourceText'];

    /**
     * Specifies whether the component is disabled.
     */
    isDisabled?: boolean;

    /**
     * Specifies whether the component is readonly.
     */
    isReadonly?: boolean;

    /**
     * Specifies for accessibility purposes whether a value is required.
     *
     * Setting this property to <code>false</code> means that a value is not required to be
     * committed by the user. Setting this property to <code>true</code> means that a value
     * is required to be committed by the user.
     */
    isRequired?: TextFieldInputProps['isRequired'];

    /**
     * Specifies whether to show an indicator on screen that a value is required, for example
     * before the user has committed a value.
     */
    isRequiredShown?: InlineUserAssistanceProps['isRequiredShown'];

    /**
     * Specifies the label associated with the field.
     */
    label: string;

    /**
     * Specifies where the label is positioned relative to the field.
     */
    labelEdge?: 'inside' | 'start' | 'top' | 'none';

    /**
     * Specifies the width of the label when <code>labelEdge</code> is <code>"start"</code>.
     */
    labelStartWidth?: Size;

    /**
     * Messages to show on screen that are associated with the component.
     */
    messages?: InlineUserAssistanceProps['messages'];

    /**
     * A short hint to display before the user enters a value.
     */
    placeholder?: TextFieldInputProps['placeholder'];

    /**
     * The prefix displays as regular text before the input text, and inline with it.
     * The prefix is not editable or focusable.
     */
    prefix?: string;

    /**
     * Variant style of step buttons.
     * @default directional
     */
    stepperVariant?: (typeof stepperVariants)[number];

    /**
     * The suffix displays as regular text after the input text, and inline with it.
     * The suffix is not editable or focusable.
     */
    suffix?: string;

    /**
     * Specifies how to align text within the field.
     */
    textAlign?: TextFieldInputProps['textAlign'];

    /**
     * Specifies the density of the user assistance presentation. It can be set to:
     * <ul>
     * <li><code>'efficient'</code>: Show inline and reserve space to prevent layout reflow when user
     * assistance text is displayed.</li>
     * <li><code>'reflow'</code>: Show inline. Layout will reflow when text is displayed.</li>
     * <li><code>'compact'</code>: Messages, help, hints, and required will not be shown inline; they will show in a mode that keeps the screen more compact, like a popup for the messages, and a required icon to indicate Required. </li>
     * </ul>
     */
    userAssistanceDensity?: UserAssistanceDensityType;

    /**
     * The current possibly formatted display value of the component.
     */
    value?: TextFieldInputProps['value'];

    /**
     * The style variant of the component.
     */
    variant?: TextFieldInputProps['variant'];

    /**
     * The type of virtual keyboard to display for entering a value on mobile browsers.
     * This property has no effect on desktop browsers.
     */
    virtualKeyboard?: 'auto' | 'number' | 'text';

    /**
     * Callback invoked when the user commits the entered value by either blurring or pressing Enter.
     */
    onCommit?: TextFieldInputProps['onCommit'];

    /**
     * Callback invoked each time the user changes the value of the field. For example, if the user
     * types 'abcd', this callback will be called four times with the value as 'a', 'ab', 'abc' and 'abcd'
     * respectively.
     */
    onInput: TextFieldInputProps['onInput'];
  };

type FocusSelectHandle = FocusableHandle & SelectableHandle;

export const stepperVariants = ['directional', 'quantitative'] as const;

export const NumberInputText = forwardRef(
  (
    {
      'aria-describedby': ariaDescribedBy,
      'aria-valuemax': ariaValueMax,
      'aria-valuemin': ariaValueMin,
      'aria-valuenow': ariaValueNow,
      'aria-valuetext': ariaValueText,
      assistiveText,
      autoComplete = 'off',
      autoFocus = false,
      columnSpan,
      helpSourceLink,
      helpSourceText,
      isDisabled: propIsDisabled,
      isReadonly: propIsReadonly,
      isRequired = false,
      isRequiredShown,
      isStepDownDisabled: propIsStepDownDisabled,
      isStepUpDisabled: propIsStepUpDisabled,
      hasSteppers = false,
      stepperVariant = 'directional',
      label,
      labelEdge: propLabelEdge,
      labelStartWidth: propLabelStartWidth,
      messages,
      placeholder,
      prefix,
      suffix,
      textAlign: propTextAlign,
      userAssistanceDensity: propUserAssistanceDensity,
      value,
      variant = 'default',
      virtualKeyboard,
      onInput,
      onCommit,
      onSpin,
      onSpinComplete,
      onStep,
      testId
    }: Props,
    ref?: Ref<FocusSelectHandle>
  ) => {
    const { currentCommitValue, dispatch } = useCurrentValueReducer({ value });
    const onInputAndDispatch = useCallback(
      (detail: ValueUpdateDetail<string>) => {
        dispatch({ type: 'input', payload: detail.value });
        onInput?.(detail);
      },
      [onInput, dispatch]
    );
    const onCommitAndDispatch = useCallback(
      (detail: ValueUpdateDetail<string>) => {
        dispatch({ type: 'commit', payload: detail.value });
        onCommit?.(detail);
      },
      [onCommit, dispatch]
    );
    const {
      isDisabled: isFormDisabled,
      isReadonly: isFormReadonly,
      labelEdge: formLabelEdge,
      labelStartWidth: formLabelStartWidth,
      textAlign: formTextAlign,
      userAssistanceDensity: formUserAssistanceDensity
    } = useFormContext();
    // default to FormContext values if component properties are not specified
    const isDisabled = propIsDisabled ?? isFormDisabled;
    const isReadonly = propIsReadonly ?? isFormReadonly;
    const labelEdge = propLabelEdge ?? formLabelEdge;
    const labelStartWidth = propLabelStartWidth ?? formLabelStartWidth;
    const textAlign = propTextAlign ?? formTextAlign ?? defaultTextAlignForVariant(variant);
    const userAssistanceDensity = propUserAssistanceDensity ?? formUserAssistanceDensity;

    const enabledElementRef = useRef<HTMLInputElement>(null);
    const readonlyElementRef = useRef<HTMLDivElement>(null);

    const {
      focusProps,
      isFocused,
      methods: focusMethods
    } = useFocusableTextField<HTMLInputElement, HTMLDivElement>({
      isDisabled,
      isReadonly,
      enabledElementRef,
      readonlyElementRef
    });

    const { methods: selectMethods } = useSelectableTextField<HTMLInputElement>(enabledElementRef);

    useImperativeHandle(ref!, () => mergeProps(focusMethods, selectMethods), [
      focusMethods,
      selectMethods
    ]);

    const {
      baseId,
      formFieldContext,
      inputProps,
      labelProps,
      textFieldProps,
      userAssistanceProps
    } = useTextField({
      ariaDescribedBy,
      helpSourceLink,
      helpSourceText,
      isDisabled,
      isFocused,
      isReadonly,
      isRequiredShown,
      labelEdge,
      messages,
      styleVariant: variant,
      userAssistanceDensity,
      value
    });
    // Disable the steppers if the number input is disabled.
    const isStepDownDisabled = isDisabled || propIsStepDownDisabled;
    const isStepUpDisabled = isDisabled || propIsStepUpDisabled;

    // Set focus to the input when a step is requested by clicking/holding down a step button.
    // For Arrow up/down, it already has focus so no action is needed.
    // Note: for readonly there are no steppers, for disabled stepping is disabled, so this only
    // applies to enabled case.
    const handleStep = useCallback(
      (e: StepEvent) => {
        if (!isFocused) {
          enabledElementRef.current?.focus();
        }
        onStep?.(e);
      },
      [onStep, isFocused, enabledElementRef]
    );

    // if hasSteppers, keyboardHandlerProps will be spread onto TextFieldInput to handle
    // stepping functionality via the arrow up or down keys.
    // also, pointerIncreaseHandlerProps and pointerDecreaseHandlerProps will be spread to
    // the corresponding StepButton
    const { keyboardHandlerProps, pointerIncreaseHandlerProps, pointerDecreaseHandlerProps } =
      useSpinning({
        isStepDownDisabled,
        isStepUpDisabled,
        onSpin,
        onSpinComplete,
        onStep: handleStep
      });

    const DecreaseButton = hasSteppers ? (
      <StepButton
        direction="decrease"
        isDisabled={isStepDownDisabled}
        {...pointerDecreaseHandlerProps}>
        {stepperVariant === 'directional' ? <DecrementIcon /> : <MinusIcon />}
      </StepButton>
    ) : undefined;

    const IncreaseButton = hasSteppers ? (
      <StepButton
        direction="increase"
        isDisabled={isStepUpDisabled}
        {...pointerIncreaseHandlerProps}>
        {stepperVariant === 'directional' ? <IncrementIcon /> : <PlusIcon />}
      </StepButton>
    ) : undefined;

    // when stepperVariant is quantitative, the decrease button is placed in the startContent slot
    const startContent =
      hasSteppers && stepperVariant === 'quantitative' ? DecreaseButton : undefined;

    // when stepperVariant is directional, both buttons are wrapped in Steppers and placed in the endContent slot
    // if it equals quantitative, only the increase button is placed in the endContent slot
    const endContent = hasSteppers ? (
      stepperVariant === 'directional' ? (
        <Steppers>
          {DecreaseButton}
          {IncreaseButton}
        </Steppers>
      ) : (
        IncreaseButton
      )
    ) : undefined;

    const hasInsideLabel = label !== undefined && labelEdge === 'inside';
    // if has steppers, and is not iOS or not Safari
    // JET-64871 the workaround for the iOS Safari VoiceOver bug with spinbutton role
    // is to not render the role or the associated wai-aria value props when iOS Safari.
    const needsSpinbuttonRole = hasSteppers && !isIosSafari();

    const {
      shouldRenderPrefix,
      shouldRenderSuffix,
      prefixProps,
      suffixProps,
      valuePrefixSuffix,
      ariaLabelledBy
    } = usePrefixSuffix({
      baseId,
      hasEndContent: endContent !== undefined,
      hasInsideLabel,
      hasStartContent: startContent !== undefined,
      hasValue: formFieldContext.hasValue,
      isDisabled,
      isFocused,
      labelId: labelProps.id,
      prefix,
      suffix,
      value
    });

    const renderedPrefix = shouldRenderPrefix ? <PrefixSuffix {...prefixProps} /> : undefined;
    const renderedSuffix = shouldRenderSuffix ? <PrefixSuffix {...suffixProps} /> : undefined;
    const labelComp = labelEdge !== 'none' ? <Label {...labelProps}>{label}</Label> : undefined;

    const fieldLabelProps = {
      label: labelEdge !== 'none' ? labelComp : undefined,
      labelEdge: labelEdge !== 'none' ? labelEdge : undefined,
      labelStartWidth: labelEdge !== 'none' ? labelStartWidth : undefined,
      labelText: labelEdge === 'inside' && !isDisabled ? label : undefined
    };

    const ariaLabel = labelEdge === 'none' ? label : undefined;
    const isInlineDensity =
      userAssistanceDensity === 'efficient' || userAssistanceDensity === 'reflow';

    const inlineUserAssistance = isInlineDensity ? (
      isDisabled ? (
        // save space for user assistance if density is 'efficient', even though we don't
        // render user assistance for disabled fields
        userAssistanceDensity !== 'efficient' ? undefined : (
          <InlineUserAssistance
            userAssistanceDensity={userAssistanceDensity}
            {...userAssistanceProps}
          />
        )
      ) : isReadonly ? (
        <InlineUserAssistance
          fieldLabel={label}
          messages={messages}
          userAssistanceDensity={userAssistanceDensity}
          {...userAssistanceProps}
        />
      ) : (
        <InlineUserAssistance
          assistiveText={assistiveText}
          fieldLabel={label}
          helpSourceLink={helpSourceLink}
          helpSourceText={helpSourceText}
          messages={messages}
          isRequiredShown={isRequiredShown}
          userAssistanceDensity={userAssistanceDensity}
          {...userAssistanceProps}
        />
      )
    ) : undefined;

    const anchorRef = useRef<HTMLDivElement>(null);

    const compactUserAssistance =
      userAssistanceDensity === 'compact' ? (
        <CompactUserAssistance
          anchorRef={anchorRef}
          messages={messages}
          assistiveText={assistiveText}
          {...userAssistanceProps}
        />
      ) : undefined;

    if (isReadonly) {
      const mainContent = (
        <ReadonlyTextFieldInput
          aria-describedby={inputProps['aria-describedby']}
          aria-label={ariaLabel}
          aria-labelledby={labelProps.id}
          as="div"
          autoFocus={autoFocus}
          elementRef={readonlyElementRef}
          formVariant={variant}
          onBlur={inputProps.onBlur}
          onFocus={inputProps.onFocus}
          textAlign={textAlign}
          value={valuePrefixSuffix}
          hasEmptyLabel={label === '' && labelEdge === 'none'}
          hasInsideLabel={hasInsideLabel}
        />
      );
      return (
        <FormFieldContext.Provider value={formFieldContext}>
          <ReadonlyTextField
            role="presentation"
            columnSpan={columnSpan}
            compactUserAssistance={compactUserAssistance}
            inlineUserAssistance={inlineUserAssistance}
            onBlur={focusProps.onFocusOut}
            onFocus={focusProps.onFocusIn}
            ref={anchorRef}
            mainContent={mainContent}
            testId={testId}
            {...fieldLabelProps}></ReadonlyTextField>
        </FormFieldContext.Provider>
      );
    }

    // If ariaValueText is the same as ariaValueNow.toString, no need to show it.
    const valueText =
      ariaValueNow === undefined
        ? ariaValueText
        : ariaValueNow.toString() !== ariaValueText
        ? ariaValueText
        : undefined;

    const mainContent = (
      <>
        {renderedPrefix}
        <TextFieldInput
          aria-labelledby={ariaLabelledBy}
          aria-label={ariaLabel}
          aria-valuemax={needsSpinbuttonRole ? ariaValueMax : undefined}
          aria-valuemin={needsSpinbuttonRole ? ariaValueMin : undefined}
          aria-valuenow={needsSpinbuttonRole ? ariaValueNow : undefined}
          aria-valuetext={needsSpinbuttonRole ? valueText : undefined}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          currentCommitValue={currentCommitValue}
          hasEmptyLabel={label === '' && labelEdge === 'none'}
          hasEndContent={endContent !== undefined}
          hasInsideLabel={hasInsideLabel}
          hasPrefix={renderedPrefix !== undefined}
          hasStartContent={startContent !== undefined}
          hasSuffix={renderedSuffix !== undefined}
          inputRef={enabledElementRef}
          isRequired={isRequired}
          placeholder={placeholder}
          role={needsSpinbuttonRole ? 'spinbutton' : undefined}
          textAlign={textAlign}
          type={isMobile() ? virtualKeyboard : undefined}
          value={isDisabled ? valuePrefixSuffix : value}
          onCommit={onCommitAndDispatch}
          onInput={onInputAndDispatch}
          {...inputProps}
          {...(hasSteppers ? keyboardHandlerProps : {})}
        />
        {renderedSuffix}
      </>
    );

    return (
      <FormFieldContext.Provider value={formFieldContext}>
        <TextField
          startContent={startContent}
          endContent={endContent}
          columnSpan={columnSpan}
          compactUserAssistance={compactUserAssistance}
          inlineUserAssistance={inlineUserAssistance}
          mainContent={mainContent}
          onBlur={focusProps.onFocusOut}
          onFocus={focusProps.onFocusIn}
          mainFieldRef={anchorRef}
          hasZeroStartMargin={hasSteppers && stepperVariant === 'quantitative'}
          testId={testId}
          {...textFieldProps}
          {...fieldLabelProps}
        />
      </FormFieldContext.Provider>
    );
  }
);

/**
 * Helper function to determine whether the current device is a mobile device
 * @returns true if running on a mobile device, false otherwise
 */
function isMobile() {
  const deviceType = getClientHints().deviceType;
  return deviceType === 'phone' || deviceType === 'tablet';
}
/**
 * Helper function to determine if the platform is iOS and the Browser is Safari.
 * Exported for usage in local tests.
 * @returns true if the platform is iOS, false otherwise
 */
export function isIosSafari() {
  return getClientHints().platform === 'ios' && getClientHints().browser === 'safari';
}
